"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAutosaveContext } from "./AutosaveManager";
import { 
  Save, 
  CheckCircle, 
  Wifi,
  WifiOff,
  Clock
} from "lucide-react";

export function AutosaveIndicator() {
  const { autosaveState } = useAutosaveContext();
  const [isVisible, setIsVisible] = React.useState(false);
  const [showMessage, setShowMessage] = React.useState(false);

  // Show indicator when saving or recently saved
  React.useEffect(() => {
    if (autosaveState.isAutoSaving || autosaveState.lastSavedAt) {
      setIsVisible(true);
      
      if (!autosaveState.isAutoSaving && autosaveState.lastSavedAt) {
        setShowMessage(true);
        // Hide success message after 3 seconds
        const timer = setTimeout(() => {
          setShowMessage(false);
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [autosaveState.isAutoSaving, autosaveState.lastSavedAt]);

  if (!isVisible) return null;

  const getTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    return `${Math.floor(diffMins / 60)}h ago`;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.9 }}
        className="fixed bottom-6 right-6 z-40"
      >
        <motion.div
          className="bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg shadow-lg px-4 py-3 flex items-center space-x-3"
          whileHover={{ scale: 1.02 }}
        >
          {/* Status Icon */}
          <div className="flex items-center">
            {autosaveState.isAutoSaving ? (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-5 h-5 text-blue-600"
              >
                <Save className="w-5 h-5" />
              </motion.div>
            ) : autosaveState.lastSavedAt ? (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-5 h-5 text-green-600"
              >
                <CheckCircle className="w-5 h-5" />
              </motion.div>
            ) : (
              <WifiOff className="w-5 h-5 text-red-500" />
            )}
          </div>

          {/* Status Text */}
          <div className="flex flex-col">
            <span className="text-sm text-gray-900">
              {autosaveState.isAutoSaving ? (
                'Saving...'
              ) : autosaveState.lastSavedAt ? (
                'Saved'
              ) : (
                'Not saved'
              )}
            </span>
            
            {autosaveState.lastSavedAt && !autosaveState.isAutoSaving && (
              <span className="text-xs text-gray-500">
                {getTimeAgo(autosaveState.lastSavedAt)}
              </span>
            )}
          </div>

          {/* Unsaved Changes Indicator */}
          {autosaveState.hasUnsavedChanges && !autosaveState.isAutoSaving && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-2 h-2 bg-orange-500 rounded-full"
            />
          )}
        </motion.div>

        {/* Success Message */}
        <AnimatePresence>
          {showMessage && !autosaveState.isAutoSaving && autosaveState.lastSavedAt && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.9 }}
              className="absolute -top-16 left-1/2 transform -translate-x-1/2 bg-green-100 border border-green-200 text-green-800 px-3 py-2 rounded-lg text-xs whitespace-nowrap shadow-sm"
            >
              ✓ Progress automatically saved
              {/* Speech bubble arrow */}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-green-200" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}

// Hook to trigger exit intent detection
export function useExitIntent() {
  const [showExitIntent, setShowExitIntent] = React.useState(false);

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleMouseLeave = (e: MouseEvent) => {
      // Only trigger if mouse leaves from the top of the viewport
      if (e.clientY <= 0) {
        timeoutId = setTimeout(() => {
          setShowExitIntent(true);
        }, 100); // Small delay to avoid false triggers
      }
    };

    const handleMouseEnter = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // This will show browser's native dialog
      e.preventDefault();
      e.returnValue = '';
      setShowExitIntent(true);
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const closeExitIntent = () => setShowExitIntent(false);

  return { showExitIntent, closeExitIntent };
}